// De dónde sale la marca del portal de un cliente (ronda 2 de la tanda iVirtual, D11-D12, 09/10).
//
// Tres opciones, y la primera es el default: el portal hereda la marca de la AGENCIA. Una marca propia
// por cliente sigue siendo posible, pero se elige explícitamente. Hasta esta ronda había un solo
// formulario de override, y una agencia sin marca propia (iVirtual) terminó cargando la suya cliente por
// cliente: 27 veces el mismo logo, que además el avatar del Listado mostraba en todas las filas.
//
// No hay un campo «fuente» en el backend: la opción se DEDUCE del override guardado y se GUARDA como
// override (`PUT client/<id>/portal/config/` con `branding`). Lógica pura, sin React: la usan la zona
// Apariencia, la pestaña Portal y la vista previa.

export const BRAND_SOURCE_AGENCY = 'AGENCY';
export const BRAND_SOURCE_ICON = 'ICON';
export const BRAND_SOURCE_CUSTOM = 'CUSTOM';
export const BRAND_SOURCES = [BRAND_SOURCE_AGENCY, BRAND_SOURCE_ICON, BRAND_SOURCE_CUSTOM];

const EMPTY_BRANDING = {
    logo_url: '',
    display_name_for_clients: '',
    brand_primary_color: '',
    brand_secondary_color: '',
};
const BRANDING_KEYS = Object.keys(EMPTY_BRANDING);

/**
 * La opción que corresponde a lo guardado: sin override → la agencia; el logo del portal ES el ícono
 * del cliente → «Usar el ícono»; cualquier otro override → marca propia. «Usar el ícono» guarda la
 * MISMA URL (no un vínculo): si después cambia el ícono, el portal conserva el anterior y la pantalla
 * pasa a decir «marca propia», que es lo que de verdad hay.
 */
export function deriveBrandSource(branding, iconUrl) {
    const current = branding || {};
    if (!BRANDING_KEYS.some((key) => current[key])) return BRAND_SOURCE_AGENCY;
    if (iconUrl && current.logo_url === iconUrl) return BRAND_SOURCE_ICON;
    return BRAND_SOURCE_CUSTOM;
}

/**
 * Lo que se guarda (y lo que ve la vista previa) con cada opción, a partir del formulario:
 * - la agencia: todo vacío, también el nombre visible — el cliente hereda la marca entera;
 * - el ícono: el logo es el ícono del cliente; nombre y colores, los del formulario (vacío = los de la
 *   agencia);
 * - marca propia: el formulario tal cual.
 */
export function brandingForSource(source, branding, iconUrl) {
    const current = {...EMPTY_BRANDING, ...pickBranding(branding)};
    if (source === BRAND_SOURCE_AGENCY) return {...EMPTY_BRANDING};
    if (source === BRAND_SOURCE_ICON) return {...current, logo_url: iconUrl || ''};
    return current;
}

/** ¿La agencia tiene una marca que heredar? (`general_data` de `get-agency-data/`). El nombre no
 * cuenta: el backend lo manda resuelto al de la agencia aunque nunca se haya cargado. */
export function agencyHasBrand(agencyData) {
    return !!(agencyData && (agencyData.logo_url || agencyData.brand_primary_color
        || agencyData.brand_secondary_color));
}

// --- Vista previa antes de guardar --------------------------------------------------------------------
//
// La vista previa es el portal de verdad en un iframe (`/client/:id/preview`), que lee lo GUARDADO. Para
// que la opción elegida se vea antes de guardar, la zona Apariencia le manda el borrador por
// `postMessage`. El iframe es del mismo origen; igual, cada lado acepta sólo mensajes de ese origen y
// de la ventana que corresponde (el padre o ese iframe), y del borrador se toman sólo las cuatro claves
// de la marca, como texto.

export const PREVIEW_READY_MESSAGE = 'dashbo:portal-preview-ready';
export const PREVIEW_BRANDING_MESSAGE = 'dashbo:portal-preview-branding';

/** El padre: manda el borrador al iframe, si ya existe. */
export function postPreviewBranding(frame, branding) {
    const target = frame && frame.contentWindow;
    if (!target) return;
    target.postMessage({type: PREVIEW_BRANDING_MESSAGE, branding: pickBranding(branding)},
        window.location.origin);
}

/** El padre: ¿es el aviso de «listo» de SU iframe? (el iframe recién montado pide el borrador). */
export function isPreviewReadyMessage(event, frame) {
    return !!(event && frame && event.origin === window.location.origin
        && event.source === frame.contentWindow
        && event.data && event.data.type === PREVIEW_READY_MESSAGE);
}

/** El iframe: avisa al padre que ya escucha. */
export function notifyPreviewReady() {
    if (window.parent && window.parent !== window) {
        window.parent.postMessage({type: PREVIEW_READY_MESSAGE}, window.location.origin);
    }
}

/** El iframe: el borrador si el mensaje es del padre y de este origen; si no, `null`. */
export function readPreviewBrandingMessage(event) {
    if (!event || event.origin !== window.location.origin || event.source !== window.parent
        || window.parent === window) return null;
    const data = event.data;
    if (!data || data.type !== PREVIEW_BRANDING_MESSAGE || !data.branding
        || typeof data.branding !== 'object') return null;
    return pickBranding(data.branding);
}

function pickBranding(branding) {
    const picked = {};
    BRANDING_KEYS.forEach((key) => {
        const value = branding && branding[key];
        picked[key] = typeof value === 'string' ? value : '';
    });
    return picked;
}
